import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { isDate } from 'date-fns';
import { flatMap } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { withoutDuplicates } from 'src/util/array';
import { isObservable, toObservable } from 'src/util/observable';
import { isPromise } from 'src/util/promise';
import { getOrElse } from 'src/util/values';

const ERROR_CODE_MESSAGE_KEY_PREFIX = '__message_';
export function getValidationStateForObject(
  formControl: AbstractControl,
  defaultErrorCodeMessages: { [key: string]: string } | undefined,
  formControlPredicate: (c: AbstractControl) => boolean,
): ValidationErrors | undefined {
  // Instead of using {} as default argument, we want to allow nulls
  defaultErrorCodeMessages = defaultErrorCodeMessages == null ? {} : defaultErrorCodeMessages;

  if (formControl.errors == null || !formControlPredicate(formControl)) {
    return undefined;
  }

  const errorMessages: { [key: string]: string } = Object.keys(formControl.errors).reduce(
    (errorCodeMessages: any, errorCode) => {
      // First: check to see if there is a validation message override specified
      if (errorCode.indexOf(ERROR_CODE_MESSAGE_KEY_PREFIX) === 0) {
        const messageKey = errorCode.substring(ERROR_CODE_MESSAGE_KEY_PREFIX.length);
        // tslint:disable-next-line: no-non-null-assertion
        errorCodeMessages[messageKey] = formControl.errors![errorCode];
      } else {
        // tslint:disable-next-line: no-non-null-assertion
        const errorCodeResult = formControl.errors![errorCode];
        // tslint:disable-next-line: no-string-literal
        const errorCodeResultMessage = errorCodeResult['message'];
        const errorCodeMessage: string | null =
          // Second: get 'message' property from error code object (if one exists)
          errorCodeResultMessage != null
            ? errorCodeResultMessage
            : // Third: use a default error code message if one exists
            // tslint:disable-next-line: no-non-null-assertion
            defaultErrorCodeMessages![errorCode] != null
            ? // tslint:disable-next-line: no-non-null-assertion
              defaultErrorCodeMessages![errorCode]
            : // Default: set error code message to 'null' - we still want error code to appear
              null;
        errorCodeMessages[errorCode] = errorCodeMessage;
      }
      return errorCodeMessages;
    },
    {},
  );

  return Object.keys(errorMessages).length > 0 ? errorMessages : undefined;
}

export interface ValidationState {
  errors?: ValidationErrors;
  children?: { [key: string]: ValidationState };
}

export interface ValidationStateCollapsed {
  [key: string]: { [key: string]: string } | undefined;
}

/**
 * Get the validation state tree for a form control given a validation message
 * configuration in the same structure.
 */
export function getValidationState(
  formControl: AbstractControl,
  defaultValidationMessages: { [key: string]: string } | null = null,
  formControlPredicate: ((c: AbstractControl) => boolean) | null = null,
  deep: boolean = false,
): ValidationState {
  defaultValidationMessages = getOrElse(defaultValidationMessages, {});
  formControlPredicate = getOrElse(formControlPredicate, () => true);
  const errors = getValidationStateForObject(
    formControl,
    defaultValidationMessages,
    formControlPredicate,
  );
  if (deep) {
    let children: { [key: string]: ValidationState } | null = null;
    if (formControl instanceof FormGroup || formControl instanceof FormArray) {
      children = Object.keys(formControl.controls).reduce(
        (childValidationState: any, childControlName) => {
          const childControl = formControl.get(childControlName) as AbstractControl;
          childValidationState[childControlName] = getValidationState(
            childControl,
            defaultValidationMessages,
            formControlPredicate,
            deep,
          );
          return childValidationState;
        },
        {},
      );
    }
    if (children != null && Object.keys(children).length > 0) {
      return { errors, children };
    }
  }
  return { errors };
}

export function collapseValidationState(
  errorMessages: ValidationState,
  pathName: string = 'root',
): ValidationStateCollapsed {
  const collapsedResult: ValidationStateCollapsed = {};
  collapsedResult[pathName] = errorMessages.errors;
  if (errorMessages.children != null) {
    return Object.keys(errorMessages.children).reduce((collapsed, child) => {
      // tslint:disable-next-line: no-non-null-assertion
      const childMValidationMessages = errorMessages.children![child];
      const path = `${pathName}.${child}`;
      const childCollapsed = collapseValidationState(childMValidationMessages, path);
      return Object.assign(collapsed, childCollapsed);
    }, collapsedResult);
  }
  return collapsedResult;
}

export function getValidationStateChangesDeep(
  control: AbstractControl,
  defaultValidationMessages: { [key: string]: string } | null = null,
  formControlPredicate: ((c: AbstractControl) => boolean) | null = null,
): Observable<ValidationState> {
  return getValidationStateChanges(control, defaultValidationMessages, formControlPredicate, true);
}

export function getValidationStateChanges(
  control: AbstractControl,
  defaultValidationMessages: { [key: string]: string } | null = null,
  formControlPredicate: ((c: AbstractControl) => boolean) | null = null,
  deep: boolean = false,
) {
  return control.statusChanges.pipe(
    startWith(control.status),
    map(() => getValidationState(control, defaultValidationMessages, formControlPredicate, deep)),
  );
}

function addMessage(
  validationErrors: ValidationErrors | undefined,
  message: string,
): ValidationErrors | null {
  if (validationErrors != null) {
    const errorCodeObjectKey = Object.keys(validationErrors).find(
      (key) => validationErrors[key] instanceof Object,
    );
    if (errorCodeObjectKey != null) {
      validationErrors[errorCodeObjectKey].message = message;
    } else {
      const errorCodeKey = Object.keys(validationErrors).find(() => true);
      if (errorCodeKey == null) {
        throw new Error(
          'Error setting error message for validator. Expected a validation object result that had at least one named key. The validation tried to set was: "' +
            message +
            '"',
        );
      }
      validationErrors[ERROR_CODE_MESSAGE_KEY_PREFIX + errorCodeKey] = message;
    }
  }
  return validationErrors != null ? validationErrors : null;
}

export function withErrorMessage(
  message: string | ((formControl: AbstractControl) => string),
  validatorFn: ValidatorFn | AsyncValidatorFn,
): ValidatorFn | AsyncValidatorFn {
  if (validatorFn == null) {
    throw new Error('Expected a validatorFn');
  }
  if (message == null) {
    throw new Error('Expected an error message.');
  }

  let messageFn: (formControl: AbstractControl) => string;
  if (message instanceof Function) {
    messageFn = message;
  } else {
    messageFn = () => message;
  }

  return (c: AbstractControl) => {
    const msg = messageFn(c);
    const innerResult: any = validatorFn(c);
    const innerAsyncResult = isObservable(innerResult)
      ? innerResult
      : isPromise(innerResult)
      ? toObservable(innerResult)
      : null;
    if (innerAsyncResult != null) {
      const asyncResult: Observable<ValidationErrors | null> = innerAsyncResult.pipe(
        map((value: ValidationErrors | undefined) =>
          value != null ? addMessage(value, msg) : null,
        ),
      );
      return asyncResult;
    } else {
      const result: ValidationErrors | null =
        innerResult != null ? addMessage(innerResult, msg) : null;
      return result;
    }
  };
}

export function validateIf(condition: boolean, validator: ValidatorFn): ValidatorFn {
  return (ctrl: AbstractControl) => {
    return condition ? validator(ctrl) : null;
  };
}

export function field(
  fieldName: string,
  {
    min,
    max,
    minYear,
    maxYear,
    maxDate,
    integer,
    required,
    validators,
  }: {
    min?: number;
    max?: number;
    required?: boolean;
    minYear?: number | 'current';
    maxYear?: number | 'current';
    maxDate?: Date | 'current';
    integer?: boolean;
    validators?: ValidatorFn[];
  } = {},
): ValidatorFn | null {
  const fieldValidators: ValidatorFn[] = [];
  function _validateIf(condition: any, message: string, _validator: ValidatorFn) {
    if (condition !== null && condition !== undefined && condition !== false) {
      fieldValidators.push(withErrorMessage(message, _validator));
    }
  }

  const yearReq = (val: number | undefined | 'current'): number | undefined =>
    val != null && val === 'current' ? new Date().getFullYear() : (val as number | undefined);

  const minYearReq = yearReq(minYear);
  _validateIf(
    minYearReq,
    `${fieldName} cannot be prior to ${minYearReq}.`,
    (control: AbstractControl) => {
      const dateValue: Date | undefined = isDate(control.value)
        ? (control.value as Date)
        : undefined;
      // tslint:disable-next-line: no-non-null-assertion
      if (dateValue != null && dateValue.getFullYear() < minYearReq!) {
        return {
          minYear: true,
        };
      }
      return null;
    },
  );

  const maxYearReq = yearReq(maxYear);
  _validateIf(
    maxYearReq,
    `${fieldName} cannot be after ${maxYearReq}.`,
    (control: AbstractControl) => {
      const dateValue: Date | undefined = isDate(control.value)
        ? (control.value as Date)
        : undefined;
      // tslint:disable-next-line: no-non-null-assertion
      if (dateValue != null && dateValue.getFullYear() > maxYearReq!) {
        return {
          maxYear: true,
        };
      }
      return null;
    },
  );

  _validateIf(maxDate, `${fieldName} must be in the past.`, (control: AbstractControl) => {
    const maxDateReq: Date | undefined =
      maxDate != null && maxDate === 'current' ? new Date() : (maxDate as Date | undefined);
    const dateValue: Date | undefined = isDate(control.value) ? (control.value as Date) : undefined;
    if (dateValue != null && maxDateReq != null && dateValue > maxDateReq) {
      return {
        maxYear: true,
      };
    }
    return null;
  });

  _validateIf(integer, `${fieldName} must be a whole number.`, (control: AbstractControl) => {
    if (control.value != null && !Number.isInteger(control.value)) {
      return {
        integer: false,
      };
    }
    return null;
  });
  _validateIf(required, `${fieldName} is required.`, Validators.required);
  // tslint:disable-next-line: no-non-null-assertion
  _validateIf(min, `${fieldName} must be ${min} or greater.`, Validators.min(min!));
  // tslint:disable-next-line: no-non-null-assertion
  _validateIf(max, `${fieldName} must be ${max} or less.`, Validators.max(max!));

  const validator = Validators.compose([
    ...(validators != null ? validators : []),
    ...fieldValidators,
  ]);
  return validator;
}

export function toggleErrors(
  add: boolean,
  controls: AbstractControl | AbstractControl[],
  errors: ValidationErrors,
) {
  const ctrls = controls instanceof Array ? controls : [controls];
  if (add) {
    addErrors(ctrls, errors);
  } else {
    removeErrors(ctrls, errors);
  }

  // Mark as dirty/touched because they are impacted by a validation
  // or other change in the form
  ctrls.forEach((ctrl) => {
    ctrl.markAsTouched();
    ctrl.markAsDirty();
  });
}

export function addErrors(
  controls: AbstractControl[],
  errors: ValidationErrors,
  emitEvent: boolean = true,
) {
  controls.forEach((c) => {
    const mergedErrors = {
      ...c.errors,
      ...errors,
    };
    c.setErrors(mergedErrors, { emitEvent });
  });
}
export function removeErrors(controls: AbstractControl[], errorCodes: string[] | ValidationErrors) {
  const codes = errorCodes instanceof Array ? errorCodes : Object.keys(errorCodes);

  controls.forEach((c) => {
    const errors = c.errors;
    if (errors != null) {
      codes.forEach((errorCode) => {
        delete errors[errorCode];
      });
      if (Object.keys(errors).length > 0) {
        c.setErrors(errors);
      } else if (c.errors != null) {
        c.setErrors(null);
      }
    }
  });
}

export function getValidationStateErrorsChangesDeep(
  formControl: AbstractControl,
): Observable<string[]> {
  return getValidationStateChangesDeep(formControl).pipe(map(getValidationStateErrors));
}

export function getValidationStateErrors(validationState: ValidationState): string[] {
  const collapsed = collapseValidationState(validationState);
  const errrorMessages = Object.keys(collapsed)
    .map((path) => {
      const errors = collapsed[path];
      return errors != null
        ? Object.keys(errors).map((errorCode) => {
            return errors[errorCode];
          })
        : [];
    })
    .reduce((em, pathErrorMessages) => {
      return [...em, ...pathErrorMessages];
    }, []);

  const deDuped: string[] = withoutDuplicates(errrorMessages);
  return deDuped;
}

export function getValidationErrorsChanges(
  control: AbstractControl,
  defaultValidationMessages: { [key: string]: string } | null = null,
  formControlPredicate: ((c: AbstractControl) => boolean) | null = null,
  deep: boolean = false,
) {
  return getValidationStateChanges(
    control,
    defaultValidationMessages,
    formControlPredicate,
    deep,
  ).pipe(map(getValidationStateErrors));
}

export function combineValidtionErrorsChanges(controls: AbstractControl[]) {
  return combineLatest(controls.map((c) => getValidationErrorsChanges(c))).pipe(
    map((r: string[][]) => {
      const errors = flatMap(r, (r2) => r2);
      const deDuped = withoutDuplicates(errors);
      return deDuped;
    }),
  );
}

export function getValidationErrorsChangesDeep(
  control: AbstractControl,
  defaultValidationMessages: { [key: string]: string } | null = null,
  formControlPredicate: ((c: AbstractControl) => boolean) | null = null,
) {
  return getValidationErrorsChanges(control, defaultValidationMessages, formControlPredicate, true);
}

export function toggleValidators(
  canValidateFn: (ctrl: AbstractControl) => boolean,
  control: AbstractControl,
  validators: { controlName: string; validators: ValidatorFn | ValidatorFn[] | null }[],
) {
  const controls = validators.map((v) => control.get(v.controlName));
  controls.forEach((ctrl, idx) => {
    if (ctrl != null) {
      if (canValidateFn(ctrl)) {
        ctrl.markAsDirty();
        ctrl.setValidators(validators[idx].validators);
      } else {
        ctrl.setValidators(null);
      }
      ctrl.updateValueAndValidity();
    }
  });
}
